import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "../cn.js";

/**
 * Grouping surfaces.
 *
 * Spec §28.3 asks that not every section be wrapped in a rounded card, so
 * `Card` is reserved for genuine groupings and `Section` is the default for
 * page structure.
 */

export interface SectionProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
	title?: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
}

export function Section({ title, description, action, children, className, ...props }: SectionProps) {
	return (
		<section {...props} className={cn("flex flex-col gap-4", className)}>
			{(title || action) && (
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="flex flex-col gap-1">
						{title && <h2 className="text-base font-semibold text-(--text-primary)">{title}</h2>}
						{description && <p className="max-w-prose text-sm text-(--text-secondary)">{description}</p>}
					</div>
					{action}
				</div>
			)}
			{children}
		</section>
	);
}

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
	/** Removes internal padding, for cards that host their own table or list. */
	flush?: boolean;
}

export function Card({ flush = false, className, children, ...props }: CardProps) {
	return (
		<div
			{...props}
			className={cn(
				"rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-raised)",
				"shadow-(--shadow-subtle)",
				flush && "overflow-hidden",
				!flush && "p-4 sm:p-5",
				className,
			)}
		>
			{children}
		</div>
	);
}

export interface CardHeaderProps {
	title: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
	className?: string;
}

export function CardHeader({ title, description, action, className }: CardHeaderProps) {
	return (
		<div
			className={cn(
				"flex flex-wrap items-start justify-between gap-3 border-b border-(--border-subtle) px-4 py-3 sm:px-5",
				className,
			)}
		>
			<div className="flex flex-col gap-0.5">
				<h3 className="text-sm font-semibold text-(--text-primary)">{title}</h3>
				{description && <p className="text-sm text-(--text-secondary)">{description}</p>}
			</div>
			{action}
		</div>
	);
}

export interface PageHeaderProps {
	title: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
	breadcrumb?: ReactNode;
}

export function PageHeader({ title, description, action, breadcrumb }: PageHeaderProps) {
	return (
		<header className="flex flex-col gap-3">
			{breadcrumb}
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="flex flex-col gap-1.5">
					<h1 className="text-xl font-semibold tracking-tight text-(--text-primary) sm:text-2xl">
						{title}
					</h1>
					{description && <p className="max-w-prose text-sm text-(--text-secondary)">{description}</p>}
				</div>
				{action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
			</div>
		</header>
	);
}
